import { API_BASE_URL } from '@synergy/config';
import { createAction } from '@reduxjs/toolkit';

import { AppDispatch, AppState } from '../../store/store';
import { PoolSnackbar } from '../components/snackbars/pool-snackbar';
import { WishListApi } from '../api/wish-list-api';
import { WishListDto } from '../models/dto/wish-list-dto';
import { WishListId, WishListListItem } from '../models/wish-list-list-item';
import { WishListType } from '../models/enums/wish-list-type';
import { enqueueSnackbar } from './snackbar-actions';
import { shoppingCartReceived } from './shopping-cart-actions';

const wishListApi = new WishListApi(API_BASE_URL);

export const wishListsReceived = createAction<{ lists: WishListListItem[] }>('wishList/lists-loaded');
export const wishListDetailsReceived = createAction<{ wishList: WishListDto }>('wishList/details-loaded');
export const itemRemovedFromWishlist = createAction<{ itemId: number }>('wishList/itemRemoved');
export const wishListShared = createAction<WishListDto>('wishList/shared');
export const wishListCreated = createAction<{ wishList: WishListListItem }>('wishList/created');
export const wishListDeleted = createAction<{ id: number }>('wishList/deleted');
export const wishListUpdated = createAction<{ id: number; name: string }>('wishList/updated');
export const wishListCopied = createAction<{ wishList: WishListListItem }>('wishList/copied');

export const wishListItemQuantityChanged = createAction<{
    itemId: number;
    quantity: number;
}>('wishList/item-quantity-changed');

export const fetchWishLists = () => async (dispatch: AppDispatch) => {
    const response = await wishListApi.getLists();

    if (response.success) {
        dispatch(wishListsReceived({ lists: response.result }));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const fetchWishListDetails = (id: WishListId) => async (dispatch: AppDispatch) => {
    const response = await wishListApi.getDetails(id);
    if (response.success) {
        dispatch(wishListDetailsReceived({ wishList: response.result }));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const addToWishlist =
    (wishListId: WishListId, productId: number, quantity: number) => async (dispatch: AppDispatch) => {
        const response = await wishListApi.addItem(wishListId, productId, quantity);
        if (response.success) {
            dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.added')));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const removeFromWishlist =
    (wishListId: WishListId, itemId: number) => async (dispatch: AppDispatch, _getState: () => AppState) => {
        const response = await wishListApi.deleteItem(wishListId, itemId);
        if (response.success) {
            dispatch(itemRemovedFromWishlist({ itemId }));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const setWishListArticleQuantity =
    (wishListId: WishListId, itemId: number, quantity: number) =>
    async (dispatch: AppDispatch, _getState: () => AppState) => {
        const response = await wishListApi.changeItemQuantity(wishListId, itemId, quantity);
        if (response.success) {
            dispatch(wishListItemQuantityChanged({ itemId, quantity }));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const copyWishListToCart = (id: WishListId) => async (dispatch: AppDispatch, _getState: () => AppState) => {
    const response = await wishListApi.replaceCart(id);
    if (response.success) {
        dispatch(shoppingCartReceived(response.result));
        dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.replacedCart')));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const createWishList = (name: string) => async (dispatch: AppDispatch, _getState: () => AppState) => {
    const response = await wishListApi.create(name);
    if (response.success) {
        dispatch(wishListCreated({ wishList: response.result }));
        dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.created', { name })));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const deleteWishList = (id: number) => async (dispatch: AppDispatch, _getState: () => AppState) => {
    const response = await wishListApi.delete(id);
    if (response.success) {
        dispatch(wishListDeleted({ id }));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const updateWishList =
    (id: number, name: string) => async (dispatch: AppDispatch, _getState: () => AppState) => {
        const response = await wishListApi.update(id, name);
        if (response.success) {
            dispatch(wishListUpdated({ id, name }));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const copyWishList =
    (id: WishListId, name: string) => async (dispatch: AppDispatch, _getState: () => AppState) => {
        const response = await wishListApi.copy(id, name);
        if (response.success) {
            dispatch(wishListCreated({ wishList: response.result }));
            dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.created', { name })));

            return response.result;
        }

        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        return null;
    };

export const moveWishListItem =
    (sourceWishListId: WishListId, itemId: number, targetWishListId: WishListId) =>
    async (dispatch: AppDispatch, _getState: () => AppState) => {
        const response = await wishListApi.moveItem(sourceWishListId, itemId, targetWishListId);
        if (response.success) {
            dispatch(itemRemovedFromWishlist({ itemId }));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const shareWishList =
    (
        id: number,
        type: WishListType,
        sharedAccountIds: number[],
        isSharedWithOwnAccounts: boolean,
        isSharedWithOtherAccountUsers: boolean,
        infoText: string | null
    ) =>
    async (dispatch: AppDispatch) => {
        const response = await wishListApi.share(
            id,
            type,
            sharedAccountIds,
            isSharedWithOwnAccounts,
            isSharedWithOtherAccountUsers,
            infoText
        );
        if (response.success) {
            dispatch(wishListShared(response.result));
            dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.shared')));
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };
